import React, { Component } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import 'react-toggle/style.css';
import InputField from '../../inputs/inputField.js';
import FieldModal from '../../modals/fieldModal.js';
import SelectField from '../../inputs/selectField.js';
import API from '../../../utils/API';

const dataOptions = [
    { value: 'Attendee', label: 'Attendees' },
    { value: 'Session', label: 'Sessions' },
    { value: 'Group', label: 'Groups' },
    { value: 'Email', label: 'Emails' },
    { value: 'Submission', label: 'Submissions' },
];

const chartOptions = [
    { value: 'Doughnut', label: 'Doughnut Chart' },
    { value: 'Line', label: 'Line Chart' },
    { value: 'Bar', label: 'Bar Chart' }
];

class EditReportGraphModal extends Component {
    constructor(props) {
        super(props);
        const { graph } = props;
        this.state = {
            submitting: false,
            selectedView:'',
            selectedDataOption: '',
            selectedChartOption: '',
            selectedFilterOption: '',
            name: graph.name || '',
            columnsOrder: [],
            viewSelects: this.formatViewOptions(props.customViews),
            requiredFields: {
                name: false,
                dataType: false,
                filter: false
            }
        };
    }
    componentDidMount() {
        const { graph, customViews } = this.props;
        const matchingView = Object.entries(customViews).find(([_, view]) => view._id === graph.viewID);
        if (matchingView) {
            const [index, view] = matchingView;
            let filterLabel = graph.filterBy;
            if (graph.filterBy && graph.filterBy.includes('*')) {
                const customField = view.normalizedColumns?.find(col => col.key === graph.filterBy);
                if (customField && customField.label) {
                    filterLabel = customField.label;
                }
            } else {
                const standardField = view.normalizedColumns?.find(col => col.key === graph.filterBy);
                if (standardField && standardField.label) {
                    filterLabel = standardField.label;
                } 
            }
            this.setState({
                selectedView: {
                    value: index,
                    label: view.name,
                    toggledColumns: view.toggledColumns,
                    normalizedColumns: view.normalizedColumns,
                    type: view.type,
                    _id: view._id
                },
                selectedDataOption: dataOptions.find(opt => opt.value === graph.dataType),
                selectedChartOption: chartOptions.find(opt => opt.value === graph.type),
                selectedFilterOption: { value: graph.filterBy, label: filterLabel }
            });
        }
    }

    formatViewOptions = (customViews) => {
        if (!customViews) return [];
    
        return Object.entries(customViews).map(([index, view]) => ({
            value: index,
            label: view.name,
            toggledColumns: view.toggledColumns,
            normalizedColumns: view.normalizedColumns,
            type: view.type,
            _id: view._id
        }));
    };

    handleViewChosen = (selectedOption) => {
        const { customViews } = this.props;
        const view = customViews[selectedOption.value];
    
        if (view) {
            this.setState({
                selectedView: selectedOption,
                selectedDataOption: dataOptions.find(opt => opt.value === view.type),
                selectedChartOption: chartOptions.find(opt => opt.value === this.props.graph.type),
                columnsOrder: view.columnsOrder || [],
                toggledColumns: view.toggledColumns || {},
                selectedFilterOption: { 
                    value: this.props.graph.filterBy, 
                    label: this.props.graph.filterBy 
                }
            });
        }
    };
    
    // Also add getViewColumns method to handle filter options
    getViewColumns = (selectedView) => {
        if (!selectedView || !this.props.customViews) return [];
    
        const view = this.props.customViews[selectedView.value];
        if (!view) return [];
    
        const activeColumns = view.normalizedColumns
            .filter(col => view.toggledColumns[col.key])
            .map(col => {
                if (col.key && col.key.includes('*')) {
                    return {
                        value: col.key,
                        label: col.label
                    };
                }
                return {
                    value: col.key,
                    label: col.label || col.key
                };
            })
            .sort((a, b) => a.label.localeCompare(b.label));
            
        return activeColumns;
    };

    
	getGraphDataForType = (dataType, selectedColumn, viewId) => {
		const { allEntries, customViews } = this.props;
		const entriesForType = allEntries[dataType] || [];
		const counts = {};
	
		// Check if the selected column is a timestamp field
		const timestampFields = ['createdAt', 'updatedAt', 'rsvpAt', 'registeredAt','submittedAt'];
		const isTimestampField = timestampFields.includes(selectedColumn);

        let displayLabel = selectedColumn;
        
        if (selectedColumn && selectedColumn.includes('*')) {
            const fieldMapping = customViews.find(view => view._id === viewId);
            displayLabel = fieldMapping.customFieldColumns[selectedColumn]; 
        } else {
            
            let fieldMapping;
            switch(dataType) {
                case 'Attendee': fieldMapping = this.props.attendee; break;
                case 'Session': fieldMapping = this.props.session; break;
                case 'Group': fieldMapping = this.props.group; break;
                case 'Email': fieldMapping = this.props.email; break;
                default: fieldMapping = {};
            }
            displayLabel = fieldMapping[selectedColumn] || selectedColumn;
        }
		
		entriesForType.forEach((entry) => {
			let value = entry[selectedColumn];
			
			if (typeof value === 'boolean') {
				value = value ? 'Yes' : 'No';
			}
			
			// Handle timestamp values
			let keyForCount = value;
			if (isTimestampField && value) {
				try {
					const date = new Date(value);
					// Format to date and hour only (no seconds) for display
					keyForCount = date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
				} catch (e) {
					console.log('Error parsing date:', value);
				}
			}
			
			// Use keyForCount for timestamps, otherwise use the standard value processing
			const valueKey = value === null || value === undefined || value === '' ? 
				'empty_value' : 
				(isTimestampField && value) ? 
					String(keyForCount) : 
					String(value);
			
			counts[valueKey] = (counts[valueKey] || 0) + 1;
		});

		
		const uniqueValues = Object.keys(counts);
		
		const formattedLabels = uniqueValues.map(value => {
			const count = counts[value];
			const displayValue = value === 'empty_value' ? `Blank` : value;
			return `${displayValue} (${count})`;  
		});
		
		return {
			label: displayLabel,
			data: uniqueValues.map(value => counts[value]),
			labels: formattedLabels,
		};
	};

    updateGraph = async () => {
        const { selectedDataOption, selectedChartOption, selectedFilterOption, name, selectedView } = this.state;
        const { orgId, reportId, eventId, graph, customViews } = this.props;
    
        // Validation
        if (!name || !selectedDataOption || !selectedFilterOption || !selectedView) {
            this.setState({
                requiredFields: {
                    name: !name,
                    dataType: !selectedDataOption,
                    filter: !selectedFilterOption,
                    viewName: !selectedView
                }
            });
            return;
        }
        // Get graph data based on selected view's data type and filter
        const graphData = this.getGraphDataForType(selectedDataOption.value, selectedFilterOption.value, selectedView._id);
        
        // Generate colors for the dataset
        const generateBlueShades = (count) => {
			const hue = 210;        
			const saturation = 83;  
			const baseAlpha = 0.8;   
			
			const actualCount = Math.max(count, 2);
    
			const shades = [];
			for (let i = 0; i < actualCount; i++) {
				  const lightness = 45 + ((90 - 45) * i) / (actualCount - 1);
				shades.push(`hsla(${hue}, ${saturation}%, ${lightness}%, ${baseAlpha})`);
			}
			return shades;
		};
    
        const backgroundColor = generateBlueShades(graphData.labels.length);
        const borderColor = backgroundColor.map(color => color.replace('0.7', '1'));
    
        const updatedGraph = {
            _id: graph._id,
            name,
            type: selectedChartOption.value,
            dataType: selectedDataOption.value,
            filterBy: selectedFilterOption.value,
            data: {
                labels: graphData.labels,
                datasets: [{
                    label: graphData.label,
                    data: graphData.data,
                    backgroundColor,
                  }],
            },
            viewID: selectedView._id // Use the selected view's ID
        };
        try {
            this.setState({ submitting: true });
    
            // Make API call to update the graph
            const res = await API().patch(
                `/Organizations/${orgId}/events/${eventId}/viewReports/${reportId}`, 
                { updatedGraph: updatedGraph }
            );
                
            if (res.data) {
                this.props.updateGraph(updatedGraph);
                this.props.toggle();
            } else {
                throw new Error('No data returned from API');
            }
        } catch (error) {
            console.error('Error updating graph:', error);
            this.setState({ 
                error: 'Failed to update graph',
                submitting: false 
            });
        } finally {
            this.setState({ submitting: false });
        }
    };



    render() {
        const { isOpen, toggle } = this.props;
        const {
            submitting,
            name,
            requiredFields,
            selectedDataOption,
            selectedChartOption,
            selectedFilterOption,
            columnsOrder,
            selectedView,
            viewSelects,
            viewExists,
        } = this.state;

        return (
            <div>
                <FieldModal
                    size="medium"
                    isOpen={isOpen}
                    modalTitle="Edit Chart"
                    bodyHeaderText="Update chart information below."
                    bodyDescription=""
                    overflowOff={true}
                    bodyContent={() => (
                        <>
                            <SelectField
								classes={"w-100"}
								label="View Name"
								value={selectedView}
								style={{ padding: 0 }}
								name="viewName"
								options={viewSelects}
								required={true}
								errorTrigger={requiredFields.viewName || viewExists}
								onChange={this.handleViewChosen}
								onInputChange={this.handleViewNameChange}
								errorMessage={viewExists ? "View already exists" : ""}
							/>

                            <InputField
                                label="Data Set"
                                value={selectedDataOption.label}
                                disabled={true}
                                classes="disabled"
                            />

                            <SelectField
                                label="Chart Type"
                                value={selectedChartOption}
                                options={chartOptions}
                                onChange={(selected) => this.setState({ selectedChartOption: selected })}
                                isSearchable={false}
                            />

                            <SelectField
                                label="Chart Filter"
                                value={selectedFilterOption}
                                options={
                                    (!selectedView.new && selectedView.value)
                                        ? this.getViewColumns(selectedView)
                                        : columnsOrder.map((col) => ({
                                            value: col,
                                            label: col
                                        })).sort((a, b) => a.label.localeCompare(b.label)) 
                                }
                                onChange={(selected) => this.setState({ selectedFilterOption: selected })}
                                isDisabled={!columnsOrder.length && (!selectedView.value)}
                                required={true}
                                errorTrigger={requiredFields.filter}
                            />

                            <InputField
                                value={name}
                                label="Graph Name"
                                required={true}
                                errorTrigger={requiredFields.name}
                                onChange={(e) => this.setState({ name: e.target.value })}
                            />
                        </>
                    )}
                    toggle={toggle}
                    actionButtonLabel={submitting ? 'Updating...' : 'Update'}
                    actionButton={this.updateGraph}
                    actionButtonDisabled={submitting}
                />
            </div>
        );
    }
}

export default EditReportGraphModal;